import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
// import { AuthContext } from '../../contexts/AuthContext';
import PageTitle from '../shared/PageTitle';
import { activityAPI } from "../../services/api";

// Custom styles for the superadmin dashboard
// const styles = {
//   header: {
//     color: '#1F458E'
//   },
//   subtext: {
//     color: '#666'
//   },
//   cardHeaderPrimary: {
//     backgroundColor: '#1F458E',
//     color: 'white'
//   },
//   cardHeaderSecondary: {
//     backgroundColor: '#D67836',
//     color: 'white'
//   }
// };

// const customStyles = `
//   .border-left-primary { border-left: 0.25rem solid #1F458E !important; }
//   .border-left-success { border-left: 0.25rem solid #28a745 !important; }
//   .border-left-info { border-left: 0.25rem solid #D67836 !important; }
//   .border-left-warning { border-left: 0.25rem solid #ffc107 !important; }
//   .text-primary { color: #1F458E !important; }
//   .text-info { color: #D67836 !important; }
// `;

const SuperadminDashboard = () => {
  // const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalRevenue: 0,
    subscriptionsByPlan: []
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        // Fetch summary stats from the API
        const response = await axios.get('/api/saas/stats', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          baseURL: process.env.REACT_APP_API_URL || 'https://nivaas.app'
        });

        // Ensure totalRevenue is a number
        const statsData = {
          ...response.data,
          totalRevenue: parseFloat(response.data.totalRevenue || 0)
        };

        console.log("statsData", statsData);

        setStats(statsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching superadmin stats:', error);
        toast.error('Failed to load dashboard statistics');
        setLoading(false);

        // Set default stats for UI display
        setStats({
          totalTenants: 0,
          activeTenants: 0,
          totalRevenue: 0,
          subscriptionsByPlan: []
        });
      }
    };

    fetchStats();

    // Apply settings to superadmin dashboard - COMMENTED OUT TO PREVENT INLINE STYLES
    // const root = document.documentElement;
    // root.style.setProperty('--theme-primary', '#D67836');
    // root.style.setProperty('--theme-primary-color', '#D67836');

    // Cleanup function to avoid affecting other pages
    return () => {
      // Note: We don't reset the colors on unmount as other SuperAdmin pages might need them
    };
  }, []);

  useEffect(() => {
    setRecentLoading(true);
    activityAPI
      .getRecentActivities(5)
      .then((res) => {
        setRecentActivities(res.data.activities || []);
      })
      .catch(() => setRecentActivities([]))
      .finally(() => setRecentLoading(false));
  }, []);

  const groupedActivities = {
    subscription_upgraded: [],
    org_registered: [],
    payment_failed: [],
  };

  recentActivities.forEach((activity) => {
    if (groupedActivities[activity.type]) {
      groupedActivities[activity.type].push(activity);
    }
  });

  // Step 2: Create sorted array of types based on latest timestamp
  const activityGroups = Object.entries(groupedActivities)
    .filter(([_, list]) => list.length > 0) // skip empty
    .map(([type, list]) => ({
      type,
      latest: new Date(Math.max(...list.map((a) => new Date(a.timestamp)))),
      list,
    }))
    .sort((a, b) => b.latest - a.latest); // newest type first

  const formatNumber = (value, decimals = 0) => {
    const num = parseFloat(value || 0);
    return num.toFixed(decimals);
  };

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-tachometer-alt"
        title="Superadmin Dashboard"
        className="mb-4"
      />
      <p className="mb-4 text-muted">Manage your SaaS platform and memberships</p>

      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status" style={{ color: '#1F458E' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="row">
            <div className="col-xl-3 col-md-6 mb-4">
              <div className="card border-left-primary shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                        ACTIVE ORGANIZATIONS
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.activeTenants}</div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-building fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6 mb-4">
              <div className="card border-left-success shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                        MONTHLY REVENUE
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">₹{formatNumber(stats.totalRevenue, 2)}</div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-inr fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6 mb-4">
              <div className="card border-left-info shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                        CONVERSION RATE
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {stats.totalTenants ? formatNumber((stats.activeTenants / stats.totalTenants) * 100) : 0}%
                      </div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-percent fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6 mb-4">
              <div className="card border-left-warning shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                        TOTAL ORGANIZATIONS
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.totalTenants}</div>
                    </div>
                    <div className="col-auto">
                      <i className="fas fa-users fa-2x text-gray-300"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Access */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow mb-4">
                <div className="py-3 superadmin-section d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold">Quick Access</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/superadmin/tenants" className="btn btn-primary w-100 py-3">
                        <i className="fas fa-building mr-2"></i>
                        Manage Organizations
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/superadmin/subscriptions" className="btn btn-success w-100 py-3">
                        <i className="fas fa-credit-card mr-2"></i>
                        Manage Subscriptions
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/superadmin/plans" className="btn btn-info w-100 py-3" style={{ backgroundColor: '#D67836', borderColor: '#D67836' }}>
                        <i className="fas fa-list mr-2"></i>
                        Manage Plans
                      </Link>
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3">
                      <Link to="/superadmin/settings" className="btn btn-secondary w-100 py-3">
                        <i className="fas fa-cog mr-2"></i>
                        Platform Settings
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription By Plan */}
          <div className="row">
            <div className="col-lg-6">
              <div className="card shadow mb-4">
                <div className="py-3 superadmin-section">
                  <h6 className="m-0 font-weight-bold">Subscriptions by Plan</h6>
                </div>
                <div className="card-body">
                  {stats.subscriptionsByPlan && stats.subscriptionsByPlan.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Plan</th>
                            <th>Active Subscriptions</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.subscriptionsByPlan.map((plan, index) => (
                            <tr key={index}>
                              <td>{plan.name}</td>
                              <td>{plan.count}</td>
                              <td>₹{formatNumber(plan.revenue, 2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p>No subscription data available</p>
                      <Link to="/superadmin/plans" className="btn btn-sm btn-primary">
                        Set Up Plans
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card shadow mb-4">
                <div className="py-3 superadmin-section">
                  <h6 className="m-0 font-weight-bold">Recent Activity</h6>
                </div>
                <div className="card-body">
                  {activityGroups.map((group) => (
                    <div className="mb-4" key={group.type}>
                      <h5>
                        •{" "}
                        {group.type === "org_registered" &&
                          "New Organization Registered"}
                        {group.type === "subscription_upgraded" &&
                          "Subscription Upgraded"}
                        {group.type === "payment_failed" && "Payment Failed"}
                      </h5>

                      {group.list.map((a) => (
                        <div key={a.id}>
                          <p className="mb-0">{a.message}</p>
                          <p className="text-muted">{a.time}</p>
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Optional: Show "No activity" for empty sections */}
                  {Object.entries(groupedActivities).map(([type, list]) => {
                    if (list.length === 0) {
                      return (
                        <div className="mb-4" key={type}>
                          <h5>
                            •{" "}
                            {type === "org_registered" &&
                              "New Organization Registered"}
                            {type === "subscription_upgraded" &&
                              "Subscription Upgraded"}
                            {type === "payment_failed" && "Payment Failed"}
                          </h5>
                          <p className="text-muted">No activity</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperadminDashboard; 